import React, { useEffect, useRef, useState } from "react";
import { Menu, Smartphone, X } from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";
import logo from "../assets/wonso.png";
import { useLanguage } from "../i18n/LanguageProvider";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeSwitcher from "./ThemeSwitcher";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(
    () => typeof window !== "undefined" && window.scrollY > 12,
  );
  const menuButtonRef = useRef(null);
  const navigationRef = useRef(null);
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    setIsOpen(false);
  }, [location.hash, location.pathname]);

  useEffect(() => {
    let animationFrame = 0;

    const handleScroll = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 12);
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusFrame = window.requestAnimationFrame(() => {
      navigationRef.current?.querySelector("a")?.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        menuButtonRef.current?.focus();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = [
        menuButtonRef.current,
        ...(navigationRef.current?.querySelectorAll(
          "a, button:not([disabled])",
        ) ?? []),
      ].filter(Boolean);

      if (focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      window.cancelAnimationFrame(focusFrame);
    };
  }, [isOpen]);

  useEffect(() => {
    const desktopQuery = window.matchMedia("(min-width: 841px)");
    const closeOnDesktop = ({ matches }) => {
      if (matches) {
        setIsOpen(false);
      }
    };

    desktopQuery.addEventListener("change", closeOnDesktop);
    return () => desktopQuery.removeEventListener("change", closeOnDesktop);
  }, []);

  const navigation = [
    ["Accueil", "/"],
    ["Solutions", "/solutions"],
    ["Business", "/business"],
    ["Tarifs", "/pricing"],
    ["À propos", "/about"],
    ["Contact", "/contact"],
  ];

  return (
    <header
      className={`ws-header${isScrolled ? " is-scrolled" : ""}${isOpen ? " is-menu-open" : ""}`}
      data-no-i18n="true"
    >
      <div className="ws-header__inner">
        <Link
          className="ws-header__brand"
          to="/"
          aria-label={t("WonyaSoft — Accueil")}
        >
          <img
            src={logo}
            alt=""
            className="ws-header__logo"
            width="38"
            height="38"
          />
          <span>WonyaSoft</span>
        </Link>

        <button
          ref={menuButtonRef}
          className="ws-header__menu-button"
          type="button"
          aria-expanded={isOpen}
          aria-controls="ws-main-navigation"
          aria-haspopup="true"
          aria-label={t(isOpen ? "Fermer le menu" : "Ouvrir le menu")}
          onClick={() => setIsOpen((current) => !current)}
        >
          {isOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>

        <nav
          ref={navigationRef}
          id="ws-main-navigation"
          className={`ws-header__navigation${isOpen ? " is-open" : ""}`}
          aria-label={t("Menu principal")}
        >
          <ul>
            {navigation.map(([label, route]) => (
              <li key={route}>
                <NavLink to={route} end={route === "/"}>
                  {t(label)}
                </NavLink>
              </li>
            ))}
          </ul>
          <div className="ws-header__mobile-language">
            <LanguageSwitcher />
            <ThemeSwitcher />
          </div>
          <NavLink className="ws-header__app-link" to="/appmobile">
            <Smartphone size={18} aria-hidden="true" />
            <span>{t("Application WonyaPay")}</span>
          </NavLink>
        </nav>

        <div className="ws-header__actions">
          <ThemeSwitcher />
          <LanguageSwitcher />
          <NavLink className="ws-header__app-link" to="/appmobile">
            <Smartphone size={18} aria-hidden="true" />
            <span>{t("Application WonyaPay")}</span>
          </NavLink>
        </div>
      </div>
      <button
        className={`ws-header__overlay${isOpen ? " is-visible" : ""}`}
        type="button"
        aria-label={t("Fermer le menu")}
        tabIndex={isOpen ? 0 : -1}
        onClick={() => setIsOpen(false)}
      />
    </header>
  );
};

export default Header;
